/**
 * Height reporting for embedded checkouts — the measurement half of
 * {@link useCheckoutDimensionsReporter}.
 *
 * The publisher SDK hosts the checkout in an iframe it cannot measure
 * (cross-origin), so the checkout has to tell it how tall its content currently
 * is via `appcharge_checkout_dimensions_changed`; the host sizes the iframe from
 * that number. Templates whose column can outgrow the viewport (EMBEDDED_VIEW:
 * sub-screens swapping in place, an APM form replacing another, a validation
 * error opening a row) would otherwise clip their Footer — and templates whose
 * content is SHORTER than the viewport would leave the publisher's page with
 * dead space under the checkout.
 */

/** Ignore sub-pixel churn so a rounding wobble can't ping-pong the host. */
const HEIGHT_CHANGE_THRESHOLD_PX = 1;

/**
 * Marks the element whose bottom edge ends the checkout's content — the payment
 * section, whose last child is the Footer. Set by `Payment` on `footerFollowsContent`
 * templates only, since those are the ones that let the section hug its content
 * (`paymentColumnContentHeight`); on the others the section is stretched to a
 * full-height column and its bottom says nothing about where content ends.
 */
export const CHECKOUT_CONTENT_END_ATTRIBUTE = 'data-checkout-content-end';

const CONTENT_END_SELECTOR = `[${CHECKOUT_CONTENT_END_ATTRIBUTE}]`;

/**
 * Distance from the top of the checkout to the bottom of the Footer, in px.
 *
 * Measured as the content-end element's bottom edge relative to the scroll
 * container's content top, NOT as `container.scrollHeight`: `scrollHeight` can
 * never report less than the viewport, so on a column that is stretched to fill
 * it (EMBEDDED_VIEW's payment wrapper is `flex-1`) it reads back the viewport height
 * — a constant, until the window shrinks enough for the content to overflow.
 * The host needs the real content height in both directions to size its iframe.
 *
 * `scrollTop` is added back so the answer does not depend on where the shopper
 * has scrolled to. The Footer itself is deliberately not the measured element:
 * `footerFollowsContent` pins it to the bottom edge while the form scrolls behind it, so
 * its own rect reports the viewport bottom rather than the content bottom. Its
 * parent section is in normal flow and ends where the Footer ends.
 *
 * Falls back to `scrollHeight` when nothing is marked (templates that keep a
 * full-height column), where content and viewport are the same thing anyway.
 */
export const measureCheckoutHeight = (container: HTMLElement): number => {
  const contentEnd = container.querySelector<HTMLElement>(CONTENT_END_SELECTOR);
  if (!contentEnd) return container.scrollHeight;

  const contentTop = container.getBoundingClientRect().top - container.scrollTop;
  const contentBottom = contentEnd.getBoundingClientRect().bottom;
  return Math.ceil(contentBottom - contentTop);
};

/**
 * Whether a freshly measured height is worth another `postMessage`. Guards the
 * observer-driven path: ResizeObserver and MutationObserver both fire on
 * changes that leave the total height untouched (an APM form swapping for one
 * of equal height, a class toggle), and every emit costs the host a re-layout.
 */
export const hasMeaningfulHeightChange = (previous: number | undefined, next: number): boolean => {
  if (next <= 0) return false;
  if (previous === undefined) return true;
  return Math.abs(next - previous) >= HEIGHT_CHANGE_THRESHOLD_PX;
};
